import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import type {
  LocalStorageUpload,
  BulkUploadStatus,
} from "@/types/bulk-upload-types";

const STORAGE_KEY = "slashexperts_bulk_uploads";

export const useBulkUploadStore = defineStore("bulkUpload", () => {
  const activeUploads = ref<Map<string, LocalStorageUpload>>(new Map());
  const uploadStatuses = ref<Map<string, BulkUploadStatus>>(new Map());

  // ====================================
  // PERSISTENCE: Load from localStorage
  // ====================================
  const loadFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data: Record<string, LocalStorageUpload> = JSON.parse(stored);
        activeUploads.value = new Map(Object.entries(data));
        console.log(
          `[BulkUploadStore] Loaded ${activeUploads.value.size} uploads from localStorage`
        );
      }
    } catch (error) {
      console.error(
        "[BulkUploadStore] Error loading from localStorage:",
        error
      );
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  // ====================================
  // PERSISTENCE: Save to localStorage
  // ====================================
  const saveToStorage = () => {
    try {
      const data = Object.fromEntries(activeUploads.value);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error("[BulkUploadStore] Error saving to localStorage:", error);
    }
  };

  // ====================================
  // AUTO-SYNC: Watch for changes and save
  // ====================================
  watch(
    activeUploads,
    () => {
      saveToStorage();
    },
    { deep: true }
  );

  // Add upload
  const addUpload = (upload: LocalStorageUpload) => {
    activeUploads.value.set(upload.uploadId, upload);
    // saveToStorage() is called automatically via watch
  };

  // Remove upload
  const removeUpload = (uploadId: string) => {
    activeUploads.value.delete(uploadId);
    uploadStatuses.value.delete(uploadId);
    // saveToStorage() is called automatically via watch
  };

  // Update status (in-memory only, not persisted)
  const updateStatus = (uploadId: string, status: BulkUploadStatus) => {
    uploadStatuses.value.set(uploadId, status);

    // Auto-remove completed uploads after 30 seconds
    if (status.status === "completed" || status.status === "failed") {
      setTimeout(() => {
        const currentStatus = uploadStatuses.value.get(uploadId);
        if (
          currentStatus &&
          (currentStatus.status === "completed" ||
            currentStatus.status === "failed")
        ) {
          removeUpload(uploadId);
        }
      }, 30000); // 30 seconds
    }
  };

  // Clear all
  const clearAll = () => {
    activeUploads.value.clear();
    uploadStatuses.value.clear();
    localStorage.removeItem(STORAGE_KEY);
  };

  // Computed
  const hasActiveUploads = computed(() => activeUploads.value.size > 0);
  const uploadCount = computed(() => activeUploads.value.size);

  // ====================================
  // INITIALIZE: Load on store creation
  // ====================================
  loadFromStorage();

  return {
    activeUploads,
    uploadStatuses,
    hasActiveUploads,
    uploadCount,
    loadFromStorage,
    addUpload,
    removeUpload,
    updateStatus,
    clearAll,
  };
});
